import React, { PureComponent, Fragment } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  Grid,
  Tabs,
  Tab,
  Typography,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from '@material-ui/core'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import InputLabel from '@material-ui/core/InputLabel'
import { withStyles } from '@material-ui/core/styles'
import { connect } from 'react-redux'

import {
  permissionsGroupOrder,
  permissionsGroupOrderMap,
  PermissionsList,
  roles as BuiltInRoleNames,
} from '../../utils/constants'
import { hasAccess, canEditRole, canManageEmployee } from '../../utils/api'
import EditActionComponent from '../../components/common/EditActionComponent'
import { EditableSwitch } from '../common/EditableComponents'
import { CustomTooltip } from '../common/Tooltip'
import { deleteRole } from '../../actions/company'
import { fetchEmployeesAPI } from '../../api/employee'
import { openErrorMessage } from '../../actions/message'

const BUILT_IN_ROLE_NAMES = new Set(
  Object.values(BuiltInRoleNames).map((roleName) => roleName.toLowerCase()),
)

const useStyles = (theme) => ({
  customWidth: {
    maxWidth: 250,
  },
})

class RolePermissions extends PureComponent {
  constructor(props) {
    super(props)
    this.isMountedFlag = false
    this.state = {
      selectedRole: {},
      updatedPermissions: [],
      activeTab: 'iPad',
      editMode: false,
      showInsightsV2Permissions: props.canUseInsightsV2Permissions || false,
      showBroadcastPermissions: props.canUseGlobalBroadcasts || false,
      deleteRoleModalOpen: false,
      deleteRoleUsageCount: null,
      deleteRoleLoading: false,
      deleteRoleSubmitting: false,
      deleteRoleReassignRoleId: '',
    }
  }

  componentDidMount() {
    this.isMountedFlag = true
  }

  componentWillUnmount() {
    this.isMountedFlag = false
  }

  componentDidUpdate(prevProps) {
    const { selectedRole } = this.state
    const { roles = [] } = this.props
    if (selectedRole && selectedRole.id && prevProps.roles !== roles) {
      const matchedRole = roles.find((role) => role.id === selectedRole.id)
      if (!matchedRole) {
        this.setState({
          selectedRole: {},
          updatedPermissions: [],
          editMode: false,
          deleteRoleModalOpen: false,
          deleteRoleUsageCount: null,
          deleteRoleLoading: false,
          deleteRoleSubmitting: false,
          deleteRoleReassignRoleId: '',
        })
      }
    }
  }

  setPassword = (event) => {
    this.setState({
      password: event.target.value,
    })
  }

  changeActiveTab = (event, eventKey) => {
    this.setState({
      activeTab: eventKey,
    })
  }

  handleChange(event) {
    const { roles, permissions = [] } = this.props

    const selectedRole =
      (roles &&
        roles.length &&
        roles.find((element) => {
          return element.id === event.target.value
        })) ||
      {}

    this.setState({
      selectedRole: {
        ...selectedRole,
      },
      updatedPermissions: [...permissions],
      deleteRoleModalOpen: false,
      deleteRoleUsageCount: null,
      deleteRoleLoading: false,
      deleteRoleSubmitting: false,
      deleteRoleReassignRoleId: '',
    })
  }

  isCustomRole = (roleName) => {
    return !!roleName && !BUILT_IN_ROLE_NAMES.has(roleName.toLowerCase())
  }

  canDeleteSelectedRole = () => {
    const { selectedRole } = this.state
    return (
      selectedRole &&
      selectedRole.id &&
      this.validateRole(selectedRole) &&
      this.isCustomRole(selectedRole.name)
    )
  }

  openDeleteRoleModal = async () => {
    const { selectedRole } = this.state

    if (!selectedRole || !selectedRole.id || !this.canDeleteSelectedRole()) {
      return
    }

    this.setState({
      deleteRoleModalOpen: true,
      deleteRoleUsageCount: null,
      deleteRoleLoading: true,
      deleteRoleSubmitting: false,
      deleteRoleReassignRoleId: '',
    })

    try {
      const response = await fetchEmployeesAPI({
        term: '',
        roleId: selectedRole.id,
        start: 0,
        limit: 1,
      })

      if (!this.isMountedFlag) {
        return
      }

      this.setState({
        deleteRoleUsageCount: (response && response.total) || 0,
        deleteRoleLoading: false,
      })
    } catch (error) {
      if (!this.isMountedFlag) {
        return
      }

      if (!this.state.deleteRoleModalOpen) {
        return
      }

      this.setState({
        deleteRoleModalOpen: false,
        deleteRoleLoading: false,
      })

      this.props.dispatch(
        openErrorMessage(
          (error && error.response && error.response.data && error.response.data.message) ||
            'Unable to load employees assigned to this role.',
        ),
      )
    }
  }

  closeDeleteRoleModal = () => {
    this.setState({
      deleteRoleModalOpen: false,
      deleteRoleUsageCount: null,
      deleteRoleLoading: false,
      deleteRoleSubmitting: false,
      deleteRoleReassignRoleId: '',
    })
  }

  getReassignableRoles = () => {
    const { roles = [] } = this.props
    const { selectedRole } = this.state

    return roles
      .filter((role) => {
        return role && role.id !== selectedRole.id && canManageEmployee(role)
      })
      .sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '')
      })
  }

  onChangeDeleteRoleReassign = (event) => {
    this.setState({
      deleteRoleReassignRoleId: event.target.value,
    })
  }

  deleteSelectedRole = async () => {
    const { selectedRole, deleteRoleUsageCount, deleteRoleReassignRoleId } = this.state
    const hasEmployeesAssigned = deleteRoleUsageCount > 0

    if (!selectedRole || !selectedRole.id) {
      return
    }

    if (hasEmployeesAssigned && !deleteRoleReassignRoleId) {
      return
    }

    this.setState({ deleteRoleSubmitting: true })

    try {
      const payload = hasEmployeesAssigned ? { reassignRoleId: deleteRoleReassignRoleId } : {}
      await this.props.dispatch(deleteRole(selectedRole.id, payload))

      if (!this.isMountedFlag) {
        return
      }

      this.closeDeleteRoleModal()
      this.setState({
        selectedRole: {},
        updatedPermissions: [],
        editMode: false,
      })

      if (this.props.onDeleteRole) {
        try {
          await this.props.onDeleteRole(selectedRole.id)
        } catch (refreshError) {
          // The delete already succeeded; keep the UI responsive even if the refresh fails.
        }
      }
    } catch (error) {
      if (!this.isMountedFlag) {
        return
      }

      if (!this.state.deleteRoleModalOpen) {
        return
      }

      const usageCount =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.details &&
        error.response.data.details.usageCount

      this.setState({
        deleteRoleSubmitting: false,
        deleteRoleLoading: false,
        deleteRoleUsageCount:
          usageCount !== undefined && usageCount !== null ? usageCount : deleteRoleUsageCount,
      })
    }
  }

  enableAllPermissions = () => {
    const { selectedRole, activeTab } = this.state
    const { index, permissions = [] } = this.props

    let fileteredPermissions =
      permissions &&
      permissions.length &&
      permissions
        .filter((perm) => {
          return perm.section === activeTab && perm.group !== 'Insights'
        })
        .map((perm) => perm.key)

    //const allPermissions = permissions && permissions.length && permissions.map(perm => perm.key);

    fileteredPermissions = Array.from(
      new Set([...selectedRole.permissions, ...fileteredPermissions]),
    )

    this.props
      .onSave({ ...selectedRole, permissions: fileteredPermissions }, index)
      .then((res) => {
        const updatedRole = res[index].roles.find((role) => role.id === selectedRole.id)
        this.setState({
          selectedRole: updatedRole,
        })
      })
      .catch((err) => {})
  }

  disableAllPermissions = () => {
    const { selectedRole, activeTab } = this.state
    const { index, permissions } = this.props

    let fileteredPermissions =
      (permissions &&
        permissions.length &&
        permissions.filter((perm) => {
          return perm.section === activeTab
        })) ||
      []

    fileteredPermissions = fileteredPermissions.map((perm) => perm.key)

    fileteredPermissions = selectedRole.permissions.filter(
      (val) => !fileteredPermissions.includes(val),
    )

    this.props
      .onSave({ ...selectedRole, permissions: fileteredPermissions }, index)
      .then((res) => {
        const updatedRole = res[index].roles.find((role) => role.id === selectedRole.id)
        this.setState({
          selectedRole: updatedRole,
        })
      })
      .catch((err) => {})
  }

  onSave = () => {
    const { selectedRole } = this.state
    const { index } = this.props
    this.disableEdit()

    this.props
      .onSave(selectedRole, index)
      .then((res) => {
        const updatedRole = res[index].roles.find((role) => role.id === selectedRole.id)
        this.setState({
          selectedRole: updatedRole,
        })
      })
      .catch((err) => {})
  }

  onCancel = (role) => {
    const { roles } = this.props
    const selectedRole =
      (roles &&
        roles.length &&
        roles.find((element) => {
          return element.id === role.id
        })) ||
      {}
    this.setState({
      selectedRole,
    })
    this.disableEdit()
    //
  }

  enableEdit = () => {
    this.setState({
      editMode: true,
    })
  }

  disableEdit() {
    this.setState({
      editMode: false,
    })
  }

  updatePermissionStatus = (role, status, key, filteredPermissions, groupOrder, permission) => {
    const { permissions = [] } = role
    let updatedPermissions = [...permissions]

    const isBroadcastGroup = groupOrder === 'Broadcast'
    const isWebEditBroadcastPermissionToggled = key === PermissionsList.Broadcast.WEB_EDIT_BROADCAST
    const isWebEditBroadcastPermissionEnabled = updatedPermissions.includes(
      PermissionsList.Broadcast.WEB_EDIT_BROADCAST,
    )

    if (
      groupOrder === 'Insights' &&
      permission !== 'Dashboard view in web' &&
      permission !== 'Reports view in web'
    ) {
      this.updatePermissionStatusInsight(role, status, key, filteredPermissions, updatedPermissions)
    } else if (
      isBroadcastGroup &&
      ((isWebEditBroadcastPermissionToggled && !isWebEditBroadcastPermissionEnabled) ||
        (!isWebEditBroadcastPermissionToggled && isWebEditBroadcastPermissionEnabled))
    ) {
      updatedPermissions = this.updatePermissionStatusBroadcast(
        status,
        updatedPermissions,
        filteredPermissions.map((row) => row.key),
      )
    } else {
      this.updatePermissionStatusDefault(status, key, updatedPermissions)
    }

    this.setState({
      selectedRole: {
        ...this.state.selectedRole,
        permissions: [...updatedPermissions],
      },
    })
  }

  updatePermissionStatusDefault = (status, key, updatedPermissions) => {
    if (status) {
      const index = updatedPermissions.findIndex((permission) => permission === key)
      if (index !== -1) updatedPermissions.splice(index, 1)
      if (key === 'WebPurchaseOrderManage') {
        const dependentIndex = updatedPermissions.findIndex(
          (perm) => perm === 'WebPurchaseOrderCostManage',
        )
        if (dependentIndex !== -1) updatedPermissions.splice(dependentIndex, 1)
      }
    } else {
      updatedPermissions.push(key)
    }
    this.setState({
      selectedRole: {
        ...this.state.selectedRole,
        permissions: [...updatedPermissions],
      },
    })
  }

  updatePermissionStatusInsight = (role, status, key, filteredPermissions, updatedPermissions) => {
    if (status) {
      const index = updatedPermissions.findIndex((permission) => permission === key)
      if (index !== -1) updatedPermissions.splice(index, 1)
    } else {
      filteredPermissions.map((row, i) => {
        const index = updatedPermissions.findIndex((permission) => permission === row.key)
        if (index !== -1) updatedPermissions.splice(index, 1)
      })
      updatedPermissions.push(key)
    }
  }

  updatePermissionStatusBroadcast = (status, updatedPermissions, filteredKeys) => {
    if (status) {
      updatedPermissions = updatedPermissions.filter(
        (permission) => !filteredKeys.includes(permission),
      )
    } else {
      updatedPermissions = updatedPermissions.filter(
        (permission) => !filteredKeys.includes(permission),
      )
      updatedPermissions.push(...filteredKeys)
    }
    return updatedPermissions
  }

  validateRole = (selectedRole) => {
    let isValidRole = false

    if (selectedRole) {
      isValidRole = canEditRole(selectedRole)
    }

    return isValidRole
  }

  render() {
    let { selectedRole = {}, updatedPermissions = [], activeTab, editMode } = this.state
    const { roles, classes } = this.props
    const editRole = this.validateRole(selectedRole)
    const isAdmin = selectedRole.name === 'Admin' ? true : false
    const permissionsToRender =
      updatedPermissions &&
      updatedPermissions.length &&
      updatedPermissions.filter((permission) => {
        return permission.section === activeTab
      })
    const { companyFeatures = {} } = this.props.companyReducer || {}
    const canDeleteSelectedRole = this.canDeleteSelectedRole()
    const reassignableRoles = this.getReassignableRoles()
    const hasEmployeesAssigned = this.state.deleteRoleUsageCount > 0
    let permissionsGroupOrderFiltered = this.state.showInsightsV2Permissions
      ? Array.from(permissionsGroupOrder)
      : permissionsGroupOrder.filter(
          (permissionGroupOrder) => permissionGroupOrder !== 'InsightsV2',
        )

    permissionsGroupOrderFiltered = this.state.showBroadcastPermissions
      ? Array.from(permissionsGroupOrderFiltered)
      : permissionsGroupOrderFiltered.filter(
          (permissionGroupOrder) => permissionGroupOrder !== 'Broadcast',
        )

    return (
      <Grid container>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <Tabs
            value={activeTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={this.changeActiveTab}
            centered
          >
            <Tab label="APP" value="iPad" />
            <Tab label="WEB" value="Web" />
          </Tabs>
          <Grid container className="margin-top-medium">
            <Grid item xs={12} sm={4} md={4} lg={3}>
              <Tooltip
                placement="right-start"
                title={
                  <Typography variant="div" className={'margin'}>
                    <Typography
                      variant="span"
                      className={'margin-top-small'}
                      style={{ fontSize: '14px' }}
                    >
                      There are hardcoded permissions for adding and editing roles
                    </Typography>
                    <ul>
                      <li>
                        <Typography
                          variant="span"
                          className={'margin'}
                          style={{ fontSize: '14px' }}
                        >
                          Admin roles can add and edit all roles
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant="span"
                          className={'margin'}
                          style={{ fontSize: '14px' }}
                        >
                          Manager roles can add and edit below roles
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant="span"
                          className={'margin'}
                          style={{ fontSize: '14px' }}
                        >
                          Shop Manager, Dispatcher, and Shop Dispatcher roles can add and edit below
                          roles
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant="span"
                          className={'margin'}
                          style={{ fontSize: '14px' }}
                        >
                          Budtender, Driver, Front Desk, Custom, and Inactive roles cannot add or
                          edit any roles
                        </Typography>
                      </li>
                    </ul>
                  </Typography>
                }
                classes={{ tooltip: classes.customWidth }}
              >
                <InputLabel>Select Role</InputLabel>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4} md={4} lg={3} className="select-margin">
              <Select
                value={(selectedRole && selectedRole.id) || ''}
                displayEmpty={true}
                onChange={(event) => this.handleChange(event)}
                fullWidth={true}
              >
                <MenuItem value="">Select Role</MenuItem>
                {roles &&
                  roles.length &&
                  roles.map((role, i) => {
                    return (
                      <MenuItem value={role.id} key={i}>
                        {role.name}
                      </MenuItem>
                    )
                  })}
              </Select>
            </Grid>
            {hasAccess() && selectedRole && selectedRole.id && editRole ? (
              <Grid item xs={12} sm={12} md={12} lg={6} className="role_permissions">
                <Grid
                  container
                  alignItems="flex-end"
                  justify="flex-end"
                  className="margin-top margin-top-des"
                >
                  <EditActionComponent
                    editMode={editMode}
                    onEdit={this.enableEdit}
                    onSave={this.onSave}
                    onCancel={() => this.onCancel(selectedRole)}
                  />
                  {!editMode ? (
                    <Fragment>
                      <Button className="primary-button margin" onClick={this.enableAllPermissions}>
                        Enable All
                      </Button>
                      <Button className="danger-button margin" onClick={this.disableAllPermissions}>
                        Disable All
                      </Button>
                      {canDeleteSelectedRole ? (
                        <Button className="danger-button margin" onClick={this.openDeleteRoleModal}>
                          Delete Role
                        </Button>
                      ) : null}
                    </Fragment>
                  ) : null}
                </Grid>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={12} md={12} lg={12}>
              {(permissionsGroupOrderFiltered &&
                permissionsGroupOrderFiltered.length &&
                permissionsGroupOrderFiltered.map((groupOrder, i) => {
                  let validatedCatalog = true
                  if (
                    groupOrder === 'Master Catalog' &&
                    companyFeatures &&
                    !companyFeatures.enableMasterCatalog
                  ) {
                    validatedCatalog = false
                  }

                  let validateWorkerApp = true
                  if (
                    groupOrder === 'Manufacturing' &&
                    companyFeatures &&
                    companyFeatures.availableApps
                  ) {
                    if (
                      companyFeatures.availableApps.find((app) => app === 'Worker') === undefined
                    ) {
                      validateWorkerApp = false
                    }
                  }

                  let validateBlazeCapital = true
                  if (groupOrder === 'BLAZE Capital' && !process.env.BLAZE_CAPITAL) {
                    validateBlazeCapital = false
                  }

                  const fileteredPermissions =
                    permissionsToRender &&
                    permissionsToRender.length &&
                    permissionsToRender.filter((row) => {
                      return row.group === groupOrder
                    })

                  return (
                    (validatedCatalog &&
                      validateWorkerApp &&
                      validateBlazeCapital &&
                      selectedRole &&
                      selectedRole.id &&
                      fileteredPermissions &&
                      fileteredPermissions.length && (
                        <Paper key={i} elevation={2} className="margin-top-medium">
                          <Grid item lg={12} style={{ padding: 30 }}>
                            <Typography
                              className="select-title-head"
                              variant="display1"
                              gutterBottom
                            >
                              {permissionsGroupOrderMap[groupOrder]}
                            </Typography>
                            <div className="table-scroll">
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Permission</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell className={editMode ? 'text-center' : ''}>
                                      Status
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(selectedRole &&
                                    selectedRole.id &&
                                    fileteredPermissions &&
                                    fileteredPermissions.length &&
                                    fileteredPermissions.map((row, i) => {
                                      const isCostPermission =
                                        row.key === 'WebPurchaseOrderCostManage'
                                      const isManagePermissionEnabled =
                                        selectedRole.permissions.includes('WebPurchaseOrderManage')
                                      const shouldDisableSwitch =
                                        isCostPermission && !isManagePermissionEnabled
                                      const status =
                                        selectedRole.permissions &&
                                        selectedRole.permissions.find((permission, index) => {
                                          return permission === row.key
                                        })
                                      return row.permission && row.group === groupOrder ? (
                                        <TableRow key={i}>
                                          <TableCell>
                                            <div className="flex-center">
                                              {row.permission}
                                              {row.tooltipMessage?.trim() && (
                                                <>
                                                  &nbsp;
                                                  <CustomTooltip message={row.tooltipMessage} />
                                                </>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell>{row.description}</TableCell>
                                          <TableCell className={editMode ? 'text-center' : ''}>
                                            <EditableSwitch
                                              valueToRender={status ? 'Enabled' : 'Disabled'}
                                              editMode={editMode}
                                              checked={Boolean(status)}
                                              onChange={() =>
                                                this.updatePermissionStatus(
                                                  selectedRole,
                                                  status,
                                                  row.key,
                                                  fileteredPermissions,
                                                  groupOrder,
                                                  row.permission,
                                                )
                                              }
                                              disabled={shouldDisableSwitch}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ) : null
                                    })) || (
                                    <TableRow>
                                      <TableCell>
                                        <h4 className="text-center">
                                          No Permissions for this role
                                        </h4>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          </Grid>
                        </Paper>
                      )) ||
                    null
                  )
                })) ||
                null}
            </Grid>
            {!selectedRole || !selectedRole.id ? (
              <Grid container>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Typography variant="title" gutterBottom className="margin-top-small set-font">
                    Please select a role.
                  </Typography>
                </Grid>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
        <Dialog
          open={this.state.deleteRoleModalOpen}
          onClose={this.closeDeleteRoleModal}
          maxWidth="sm"
          fullWidth
          disableBackdropClick={this.state.deleteRoleSubmitting}
          disableEscapeKeyDown={this.state.deleteRoleSubmitting}
        >
          <DialogTitle>{`Deleting role: "${selectedRole.name || ''}"`}</DialogTitle>
          <DialogContent>
            {this.state.deleteRoleLoading ? (
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <CircularProgress size={20} />
                </Grid>
                <Grid item>
                  <Typography variant="body1">
                    Checking how many employees use this role...
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <Typography variant="body1">
                    {hasEmployeesAssigned
                      ? this.state.deleteRoleUsageCount === 1
                        ? `There is 1 employee assigned to this role. Select a replacement role to reassign them before deleting it.`
                        : `There are ${this.state.deleteRoleUsageCount} employees assigned to this role. Select a replacement role to reassign them before deleting it.`
                      : 'No employees are assigned to this role. You can delete it now.'}
                  </Typography>
                </Grid>
                {hasEmployeesAssigned ? (
                  <Grid item xs={12}>
                    <br />
                    <FormControl fullWidth>
                      <InputLabel id="delete-role-reassign-label">Reassign employees to</InputLabel>
                      <Select
                        labelId="delete-role-reassign-label"
                        value={this.state.deleteRoleReassignRoleId}
                        onChange={this.onChangeDeleteRoleReassign}
                      >
                        <MenuItem value="">
                          <em>Select a role</em>
                        </MenuItem>
                        {reassignableRoles.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!reassignableRoles.length ? (
                      <Typography variant="body2" color="error" className="margin-top-small">
                        No assignable roles are available for your permissions.
                      </Typography>
                    ) : null}
                  </Grid>
                ) : null}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDeleteRoleModal} className="button-white-and-red">
              Close
            </Button>
            <Button
              onClick={this.deleteSelectedRole}
              className="button-blue-and-white"
              style={{ padding: '6px 16px', minWidth: 160 }}
              disabled={
                this.state.deleteRoleLoading ||
                this.state.deleteRoleSubmitting ||
                this.state.deleteRoleUsageCount === null ||
                (hasEmployeesAssigned && !this.state.deleteRoleReassignRoleId) ||
                (hasEmployeesAssigned && !reassignableRoles.length)
              }
            >
              {this.state.deleteRoleSubmitting ? (
                <CircularProgress size={18} style={{ color: 'white' }} />
              ) : hasEmployeesAssigned ? (
                'Reassign and Delete'
              ) : (
                'Delete'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    )
  }
}
export default withStyles(useStyles)(
  connect((state) => ({ dispatch: state.dispatch, companyReducer: state.companyReducer }))(
    RolePermissions,
  ),
)



// WEBPACK FOOTER //
// ./src/components/RolePermissions/index.js